import { useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

import { Button } from '@components/button';
import { IconAddPlus } from '@components/icons/add-plus';
import { RelativeSpinner } from '@components/loading-spinner';
import { hideModal, Modal, useModal } from '@components/modal';
import { Select } from '@components/select';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { useAddJourneyTemplate } from 'api/journeys/hooks';
import { useWorkspaces } from 'api/workspaces/hooks';
import { routePaths } from 'ui/authenticated/navigation';

export const useGetTemplateModal = () => useModal(GetTemplateModal);

export const GetTemplateModal = ({ journeyTemplateId }: { journeyTemplateId: string }) => {
  const { t } = useScopedTranslate(locale);
  const [selectedWorkspace, setSelectedWorkspace] = useState<string | null>(null);
  const { data: workspaces } = useWorkspaces();
  const addJourneyTemplate = useAddJourneyTemplate();
  const navigate = useNavigate();

  const handleAddTemplate = async () => {
    if (selectedWorkspace) {
      const response = await addJourneyTemplate.mutateAsync({
        journeyId: journeyTemplateId,
        toWorkspaceId: selectedWorkspace,
      });
      navigate(
        generatePath(routePaths.journeyDetails, {
          journeyId: response.journeyShortId,
          workspaceId: selectedWorkspace,
          '*': null,
        }),
        { replace: true }
      );
    }
  };

  if (!workspaces) {
    return <RelativeSpinner />;
  }

  return (
    <Modal>
      <Modal.SimpleHeader title={t('getTemplate')} />
      <Modal.Content>
        <Stack space={6}>
          <Stack space={6}>
            <Text text={t('helpText')} />
            <Select
              label={t('workspace')}
              hideLabel
              options={workspaces.map((workspace) => ({
                value: workspace.shortId,
                label: workspace.title,
              }))}
              value={selectedWorkspace}
              onChange={(value) => setSelectedWorkspace(value)}
            />
          </Stack>
          <Stack orientation="horizontal" space={4} justify="flex-end">
            <Button variant="secondary" onClick={hideModal} text={t('core.cancel')} />
            <Button
              onClick={handleAddTemplate}
              disabled={addJourneyTemplate.isLoading}
              variant="primary"
              iconLeft={<IconAddPlus />}
              text={t('addJourney')}
            />
          </Stack>
        </Stack>
      </Modal.Content>
    </Modal>
  );
};

export const locale = {
  en: {
    getTemplate: 'Get template',
    helpText: 'Choose workspace to add journey to',
    addJourney: 'Add journey',
    workspace: 'Workspace',
  },
};
